import React, { useState, useEffect, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import {
  Wrench,
  Phone,
  CheckCircle2,
  ChevronRight,
  ShieldCheck,
  Star,
  MapPin,
  Clock,
  Mail,
  AlertCircle,
  Sparkles,
  ChevronLeft
} from "lucide-react";
import { servicesData, submitContactInquiry } from "@/mock";

export default function ServicePage() {
  const { serviceId } = useParams();
  const navigate = useNavigate();
  const [service, setService] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    isEmergency: "no",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  useEffect(() => {
    const foundService = servicesData.find((s) => s.id === serviceId);
    if (!foundService) {
      // Redirect to home if invalid service ID
      navigate("/");
    } else {
      setService(foundService);
      setFormData((prev) => ({
        ...prev,
        service: foundService.id,
        message: `I need a quote/appointment for ${foundService.title}.`
      }));
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [serviceId, navigate]);

  const otherServices = useMemo(() => {
    return servicesData.filter((s) => s.id !== service?.id);
  }, [service?.id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone || !formData.message) {
      setSubmitStatus("error");
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);
    try {
      await submitContactInquiry(formData);
      setSubmitStatus("success");
      setFormData({
        name: "",
        phone: "",
        email: "",
        service: service?.id || "",
        isEmergency: "no",
        message: `I need a quote/appointment for ${service?.title || ""}.`
      });
    } catch (err) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!service) {
    return (
      <div className="bg-zinc-950 min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-zinc-950 text-zinc-100 min-h-screen">
      {/* Dynamic Page Banner / Hero */}
      <section className="relative py-20 px-4 border-b border-zinc-900 bg-zinc-900/20">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={service.image}
            alt={service.title}
            className="w-full h-full object-cover object-center opacity-15 filter grayscale blur-sm"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/70 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto flex flex-col items-start">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-xs font-bold text-zinc-500 hover:text-blue-500 transition-colors uppercase tracking-widest mb-6"
          >
            <ChevronLeft className="h-4 w-4" />
            <span>Back to Home</span>
          </Link>

          <span className="text-xs font-bold text-blue-500 uppercase tracking-widest bg-blue-950/40 px-3 py-1 rounded-full border border-blue-500/10">
            KG Professional Service
          </span>

          <h1 className="text-3xl sm:text-5xl font-extrabold text-white mt-4 tracking-tight leading-tight">
            {service.title}
          </h1>

          <p className="text-zinc-400 mt-2 text-sm sm:text-base max-w-2xl font-semibold">
            {service.subtitle}
          </p>
        </div>
      </section>

      {/* Main layout: Description & Side layout */}
      <section className="py-16 px-4 max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Main Description Column */}
          <div className="lg:col-span-2 space-y-12">
            {/* 1. Main Service Image & Lead Intro */}
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden border border-zinc-900 aspect-[21/9]">
                <img src={service.image} alt={service.title} className="w-full h-full object-cover" />
              </div>
              <h2 className="text-2xl font-bold text-white">Service Overview</h2>
              <p className="text-zinc-300 leading-relaxed text-base">
                {service.description}
              </p>
            </div>

            {/* 2. Key Service Benefits */}
            <div className="bg-zinc-900/40 border border-zinc-900 rounded-2xl p-8 space-y-6">
              <h3 className="text-xl font-bold text-white flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-blue-500" />
                <span>Why Choose KG for {service.title}?</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {service.benefits.map((benefit, idx) => (
                  <div key={`benefit-${idx}`} className="flex items-start gap-2.5">
                    <CheckCircle2 className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
                    <span className="text-sm text-zinc-300 leading-relaxed">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* 3. Detailed Custom Sections (FAQ/Features/Brands) */}
            {service.details && (
              <div className="space-y-6">
                <h3 className="text-2xl font-bold text-white">{service.details.sectionTitle}</h3>
                <p className="text-zinc-400 text-sm leading-relaxed">{service.details.sectionText}</p>

                {/* Extra lists: either brands or services list */}
                {service.details.extraServices && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {service.details.extraServices.map((extra, idx) => (
                      <div key={`extra-${idx}`} className="p-4 bg-zinc-900 border border-zinc-850 rounded-xl">
                        <p className="text-sm font-bold text-zinc-200">{extra}</p>
                      </div>
                    ))}
                  </div>
                )}

                {service.details.brands && (
                  <div>
                    <h4 className="text-xs font-bold text-zinc-400 uppercase tracking-wider mb-3">
                      Automotive Brands We Specialize In:
                    </h4>
                    <div className="flex flex-wrap gap-2">
                      {service.details.brands.map((brand) => (
                        <span
                          key={brand}
                          className="px-4 py-2 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-zinc-300 font-semibold"
                        >
                          {brand}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Sidebar Area */}
          <div className="space-y-8">
            {/* Quick Contact Box */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6 shadow-xl">
              <h4 className="text-lg font-bold text-white mb-4">Request a Quote</h4>
              
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="side-name" className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">
                    Name *
                  </label>
                  <input
                    type="text"
                    id="side-name"
                    name="name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 text-xs transition-colors"
                    placeholder="Your Name"
                  />
                </div>

                <div>
                  <label htmlFor="side-phone" className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">
                    Phone *
                  </label>
                  <input
                    type="tel"
                    id="side-phone"
                    name="phone"
                    required
                    value={formData.phone}
                    onChange={handleChange}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 text-xs transition-colors"
                    placeholder="Phone Number"
                  />
                </div>

                <div>
                  <label htmlFor="side-message" className="block text-[10px] font-bold uppercase tracking-wider text-zinc-400 mb-1">
                    Vehicle Details & Message *
                  </label>
                  <textarea
                    id="side-message"
                    name="message"
                    required
                    rows={3}
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full bg-zinc-950 border border-zinc-800 rounded-xl px-4 py-2.5 text-white focus:outline-none focus:border-blue-500 text-xs transition-colors resize-none"
                    placeholder="e.g. 2012 Audi Q5, brake grinding..."
                  />
                </div>

                {submitStatus === "success" && (
                  <div className="p-3 bg-emerald-950/40 border border-emerald-500/30 rounded-xl text-emerald-400 text-xs flex items-start gap-2">
                    <CheckCircle2 className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Sent!</p>
                      <p className="text-zinc-400">Grant will contact you shortly.</p>
                    </div>
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="p-3 bg-rose-950/40 border border-rose-500/30 rounded-xl text-rose-400 text-xs flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">Error</p>
                      <p className="text-zinc-400">Please fill out all fields.</p>
                    </div>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold py-3 px-4 rounded-xl transition-all disabled:opacity-50 text-xs tracking-wider uppercase"
                >
                  {isSubmitting ? "Sending..." : "Submit Inquiry"}
                </button>
              </form>

              <div className="border-t border-zinc-800/80 mt-6 pt-6 text-center space-y-2">
                <p className="text-xs text-zinc-500">Need Immediate Help?</p>
                <a
                  href="tel:3046853724"
                  className="flex items-center justify-center gap-2 text-white font-extrabold text-base hover:text-blue-500 transition-colors"
                >
                  <Phone className="h-4 w-4 text-blue-500" />
                  <span>304-685-3724</span>
                </a>
              </div>
            </div>

            {/* Other Services Navigation Box */}
            <div className="bg-zinc-900 border border-zinc-800 rounded-2xl p-6">
              <h4 className="text-sm font-bold text-white uppercase tracking-widest mb-4">Other Services</h4>
              <div className="space-y-2">
                {otherServices.map((otherService) => (
                  <Link
                    key={otherService.id}
                    to={`/services/${otherService.id}`}
                    className="flex items-center justify-between p-3 rounded-xl hover:bg-zinc-800 text-zinc-300 hover:text-white transition-all text-xs font-semibold group"
                  >
                    <span>{otherService.title}</span>
                    <ChevronRight className="h-4 w-4 text-zinc-500 group-hover:text-blue-500 transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
